"use client";

import { useConsent } from "@packages/consent";
import { useSearch } from "@packages/framework";
import type { Geolocation, Me, SubscriptionDetail } from "@packages/sdk";
import {
  getCookieAjsAnonymousId,
  setCookieAjsAnonymousId,
  useDeviceAndUserProperties,
  useLocalStorageState,
  useRequestGeolocation,
  useRequestMe,
  useRequestSubscription,
  useSession,
} from "@packages/sdk";
import mixpanelBrowser from "mixpanel-browser";
import type { PropsWithChildren } from "react";
import { useEffect, useMemo, useState } from "react";

import type { MixpanelWithFlags } from "../lib/mixpanelContext";
import { mixpanelContext } from "../lib/mixpanelContext";
import type { HallowABFlag } from "../types/enums/HallowABFlag.enum";
import { MixpanelABFlagValidProps } from "../types/enums/MixpanelABFlagValidProps.enum";

const THREE_SECONDS_IN_MS = 3000;
const MS_BETWEEN_ATTEMPTS = 250;
const ATTEMPTS = THREE_SECONDS_IN_MS / MS_BETWEEN_ATTEMPTS;

export type MixpanelLogicFlag = {
  type: "guest" | "user" | "subscriber";
  evaluate: (props: {
    subscription?: SubscriptionDetail;
    geolocation?: Geolocation;
    user?: Me;
    searchParams?: URLSearchParams;
  }) => boolean;
};

type ABProviderProps = PropsWithChildren<{
  flags: { [key in HallowABFlag]?: MixpanelLogicFlag };
}>;

export const ABProvider = ({ children, flags }: ABProviderProps) => {
  const search = useSearch();
  const deviceAndUserProperties = useDeviceAndUserProperties();
  const [mixpanel, setMixpanel] = useState<MixpanelWithFlags | null>(null);
  const [ready, setReady] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const { isConsenting, isLoading: isConsentLoading } = useConsent();
  const { query: queryMe } = useRequestMe();
  const { query: querySubscription } = useRequestSubscription();
  const { query: queryGeolocation } = useRequestGeolocation();
  const [flagCache, setFlagCache] = useLocalStorageState<{
    [key in HallowABFlag]?: {
      isTargeted: boolean;
      variant: "control" | "treatment" | "excluded" | null;
    };
  }>({
    defaultValue: Object.keys(flags).reduce(
      (prev, key) => ({ ...prev, [key]: { isTargeted: false, variant: null } }),
      {},
    ),
    key: "flags",
  });

  useSession({
    onUnauthenticated: () => {
      mixpanel?.reset();
      setFlagCache({});
    },
  });

  //#region Context Updating
  /** @var {boolean} seenAsSubscriber - Indicates that the user has an active subscription */
  const seenAsSubscriber = !!querySubscription.data;

  const customProperties = useMemo(() => {
    const props: Record<string, string> = {};
    for (const [key, f] of Object.entries(flags)) {
      let validProp = MixpanelABFlagValidProps[key];
      if (!validProp) continue;
      if (flagCache?.[key]?.variant) {
        props[validProp] = flagCache[key].variant;
      } else if (!queryGeolocation.data) {
        props[validProp] = "false";
      } else if (f.type === "user" && !queryMe.data?.id) {
        props[validProp] = "false";
      } else if (f.type === "subscriber" && !seenAsSubscriber) {
        props[validProp] = "false";
      } else {
        props[validProp] = f.evaluate({
          subscription: querySubscription.data,
          geolocation: queryGeolocation.data,
          user: queryMe.data,
          searchParams: search,
        })
          ? "true"
          : "false";
      }
    }
    return { ...props, ...deviceAndUserProperties };
  }, [
    flags,
    querySubscription.data,
    queryGeolocation.data,
    queryMe.data,
    isConsenting,
    deviceAndUserProperties,
  ]);

  useEffect(() => {
    if (customProperties && mixpanel) {
      mixpanel.flags
        .update_context({
          custom_properties: customProperties,
        })
        .then(() => {
          const values: {
            [key in HallowABFlag]?: {
              isTargeted: boolean;
              variant: "control" | "treatment" | "excluded" | null;
            };
          } = {};
          for (const [key, f] of Object.entries(flags)) {
            const shouldCheck =
              (f.type === "guest" && !!queryGeolocation.data) ||
              (f.type === "user" && !!queryMe.data?.id) ||
              (f.type === "subscriber" && seenAsSubscriber);

            let variant = flagCache?.[key]?.variant;

            if (shouldCheck) {
              variant =
                variant ??
                mixpanel.flags.get_variant_value_sync(key, "excluded");
              values[key] = {
                isTargeted: variant === "treatment",
                variant,
              };
            } else {
              values[key] = {
                isTargeted: variant ? variant === "treatment" : false,
                variant: variant ?? null,
              };
            }
          }
          setFlagCache(values);
        });
    }
  }, [customProperties, mixpanel, deviceAndUserProperties]);

  useEffect(() => {
    if (error) {
      const newCache = {};
      for (const key of Object.keys(flags)) {
        newCache[key] = { isTargeted: false, variant: "excluded" };
      }
      setFlagCache(newCache);
    }
  }, [error]);
  //#endregion

  //#region Library Initialization
  useEffect(() => {
    if (!isConsentLoading && isConsenting) {
      try {
        mixpanelBrowser.init(process.env.DOPPLER_PUBLIC_MIXPANEL_TOKEN, {
          debug: process.env.NODE_ENV === "development",
          // @ts-ignore -- this is in the JS code, but not the types
          flags: {
            context: {
              custom_properties: customProperties,
            },
          },
          loaded: (mp) => {
            setMixpanel(mp as MixpanelWithFlags);
            const ajsAnonymousId = getCookieAjsAnonymousId();
            if (ajsAnonymousId) {
              mp.alias(ajsAnonymousId);
            } else {
              setCookieAjsAnonymousId({ value: mp.get_distinct_id() });
            }
          },
        });
      } catch (e) {
        console.error(e);
        setError(true);
      }
    } else if (!isConsentLoading && !isConsenting) {
      setError(true);
    }
  }, [isConsenting, isConsentLoading, deviceAndUserProperties]);

  useEffect(() => {
    let interval = null;
    if (mixpanel) {
      if (mixpanel.flags.are_flags_ready()) {
        setReady(true);
      } else {
        let count = 0;
        interval = setInterval(() => {
          if (++count > ATTEMPTS) {
            setError(true);
            clearInterval(interval);
          } else if (mixpanel.flags?.are_flags_ready()) {
            setReady(true);
            clearInterval(interval);
          }
        }, MS_BETWEEN_ATTEMPTS);
      }
    }

    return () => {
      clearInterval(interval);
    };
  }, [mixpanel]);
  //#endregion

  return (
    <mixpanelContext.Provider
      value={{
        flags: flagCache,
        isLoading: !ready && !error,
      }}
    >
      {children}
    </mixpanelContext.Provider>
  );
};
